import PropTypes from "prop-types";
import { Text, TextInput } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isHtmlTag, mgaRegex } from "../../../constants/helper";
import displayError from "../../../constants/utils";
import SEConfirmModal from "../../common/SEConfirmModal";
import DynamicTabbar from "../../../Tarrif/TabBar";
import "./style.scss";

const MAX_FLEETS = 10;

const FleetTabs = ({
  data,
  activeKey,
  defaultActiveKey,
  onChange,
  className,
  orientation = "vertical",
  allowAdd = false,
  onDataChange,
  onDeleteFleet,
  createItem,
  dirtyFleetKeys = {},
  onBeforeAdd,
}) => {
  const { t } = useTranslation();
  const [tabData, setTabData] = useState(data || []);
  const [newFleetName, setNewFleetName] = useState("");
  const [fleetNameError, setFleetNameError] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUnsavedAddModalOpen, setIsUnsavedAddModalOpen] = useState(false);
  const [isSavingBeforeAdd, setIsSavingBeforeAdd] = useState(false);
  const [fleetToDelete, setFleetToDelete] = useState(null);
  const firstKey = tabData?.[0]?.key;
  const [internalActiveKey, setInternalActiveKey] = useState(
    defaultActiveKey || firstKey,
  );
  const selectedKey = activeKey ?? internalActiveKey;

  useEffect(() => {
    setTabData(data || []);
  }, [data]);

  useEffect(() => {
    if (
      activeKey === undefined &&
      !tabData?.some((item) => item?.key === internalActiveKey)
    ) {
      setInternalActiveKey(defaultActiveKey || firstKey);
    }
  }, [activeKey, defaultActiveKey, firstKey, internalActiveKey, tabData]);

  const handleTabChange = (key) => {
    if (activeKey === undefined) setInternalActiveKey(key);
    onChange?.(key);
  };

  const handleTabIndexChange = (index) => {
    const key = tabData[index]?.key;
    if (key !== undefined) handleTabChange(key);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setNewFleetName("");
    setFleetNameError("");
  };

  const openAddModal = () => {
    if (tabData.length >= MAX_FLEETS) {
      displayError("error", t("fleetTabs.maxFleets"));
      return;
    }
    setIsAddModalOpen(true);
  };

  const handleAddClick = () => {
    if (tabData.length >= MAX_FLEETS) {
      displayError("error", t("fleetTabs.maxFleets"));
      return;
    }
    if (dirtyFleetKeys[selectedKey]) {
      setIsUnsavedAddModalOpen(true);
      return;
    }
    openAddModal();
  };

  const saveBeforeAdd = async () => {
    if (isSavingBeforeAdd) return;
    setIsSavingBeforeAdd(true);
    try {
      const saved = await onBeforeAdd?.(tabData[activeIndex]);
      if (saved === false) {
        setIsUnsavedAddModalOpen(false);
        return;
      }

      setIsUnsavedAddModalOpen(false);
      openAddModal();
    } finally {
      setIsSavingBeforeAdd(false);
    }
  };

  const validateFleetName = (fleetName) => {
    const trimmedName = (fleetName || "").trim();
    let nameError = "";

    if (!trimmedName) {
      nameError = t("fleetTabs.validation.required");
    } else if (isHtmlTag(fleetName)) {
      nameError = t("fleetTabs.validation.htmlTags");
    } else if (!mgaRegex.leadingTrailingInternalSpacesRegex.test(fleetName)) {
      nameError = t("fleetTabs.validation.spaces");
    } else if (trimmedName.length > 20) {
      nameError = t("fleetTabs.validation.maxLength");
    } else if (!mgaRegex.alphanumericWithSpaceUnderscoreHyphenRegex.test(trimmedName)) {
      nameError = t("fleetTabs.validation.allowedChars");
    } else if (
      tabData.some(
        (item) =>
          String(item?.label || "")
            .trim()
            .toLowerCase() === trimmedName.toLowerCase(),
      )
    ) {
      nameError = t("fleetTabs.validation.unique");
    }

    return { trimmedName, nameError };
  };

  const createFleetItem = (label) => {
    const key = `${label.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}`;

    return createItem
      ? createItem({ key, label })
      : { key, label, description: "", content: <div /> };
  };

  const addFleet = () => {
    if (tabData.length >= MAX_FLEETS) {
      displayError("error", t("fleetTabs.maxFleets"));
      return;
    }

    const { trimmedName, nameError } = validateFleetName(newFleetName);

    if (nameError) {
      setFleetNameError(nameError);
      return;
    }

    const newItem = createFleetItem(trimmedName);
    const nextData = [...tabData, newItem];

    setTabData(nextData);
    setInternalActiveKey(newItem.key);
    onDataChange?.(nextData, newItem);
    onChange?.(newItem.key);
    closeAddModal();
  };

  const closeDeleteModal = () => {
    setFleetToDelete(null);
  };

  const confirmDeleteFleet = async () => {
    const key = fleetToDelete?.key;
    if (!key) return;

    const deleted = await onDeleteFleet?.(fleetToDelete);
    if (deleted === false) return;

    const deletedIndex = tabData.findIndex((item) => item?.key === key);
    const nextData = tabData.filter((item) => item?.key !== key);

    setTabData(nextData);
    onDataChange?.(nextData);

    if (selectedKey === key) {
      const nextActiveItem =
        nextData[deletedIndex] || nextData[deletedIndex - 1];
      const nextActiveKey = nextActiveItem?.key;

      setInternalActiveKey(nextActiveKey);
      onChange?.(nextActiveKey, { reason: "delete", deletedKey: key });
    }

    closeDeleteModal();
  };

  const activeIndex = Math.max(
    0,
    tabData.findIndex((item) => item?.key === selectedKey),
  );

  return (
    <>
      <div className={`fleet-tabs ${className || ""}`}>
        <DynamicTabbar
          items={tabData}
          activeIndex={activeIndex}
          handleItemClick={handleTabIndexChange}
          handleDoubleClick={() => {}}
          removeItem={(item) => setFleetToDelete(item)}
          addItem={handleAddClick}
          itemName="label"
          itemId="key"
          tabName="fleet"
          showAddRemove={allowAdd}
          maxLabelLength={8}
        />
        {tabData.length ? (
          <div className="fleet-tabs__content">{tabData[activeIndex]?.content}</div>
        ) : (
          <Text className="fleet-tabs__empty" size="sm">
            {allowAdd
              ? t("fleetTabs.empty.addPrompt")
              : t("fleetTabs.empty.noFleets")}
          </Text>
        )}
      </div>
      <SEConfirmModal
        opened={isUnsavedAddModalOpen}
        onClose={() => setIsUnsavedAddModalOpen(false)}
        onConfirm={saveBeforeAdd}
        header={t("evcs.fleet.unsavedChanges")}
        confirmLabel={t("common.save")}
        cancelLabel={t("fleetTabs.cancel")}
        confirmLoading={isSavingBeforeAdd}
        confirmLoadingLabel={t("common.saving")}
        message={t("evcs.fleet.addConfirmation", {
          currentFleet: tabData[activeIndex]?.label || t("evcs.fleet.thisFleet"),
        })}
      />
      <SEConfirmModal
        opened={isAddModalOpen}
        onClose={closeAddModal}
        onConfirm={addFleet}
        header={t("fleetTabs.addFleet")}
        confirmLabel={t("fleetTabs.add")}
        cancelLabel={t("fleetTabs.cancel")}
        message={
          <TextInput
            label={t("fleetTabs.fleetName")}
            type="text"
            value={newFleetName}
            onChange={(event) => {
              setNewFleetName(event.currentTarget.value);
              setFleetNameError("");
            }}
            required
            maxLength={20}
            error={fleetNameError || undefined}
            className="fleet-tabs__field"
          />
        }
      />
      <SEConfirmModal
        opened={Boolean(fleetToDelete)}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteFleet}
        header={t("fleetTabs.deleteFleet")}
        confirmLabel={t("fleetTabs.ok")}
        cancelLabel={t("fleetTabs.cancel")}
        message={
          <Text>
            {t("fleetTabs.deleteConfirmation", {
              label: fleetToDelete?.label,
            })}
          </Text>
        }
      />
    </>
  );
};

FleetTabs.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      content: PropTypes.node,
    }),
  ).isRequired,
  activeKey: PropTypes.string,
  defaultActiveKey: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  allowAdd: PropTypes.bool,
  onDataChange: PropTypes.func,
  onDeleteFleet: PropTypes.func,
  createItem: PropTypes.func,
};

export default FleetTabs;
